import React from 'react';

import ReactDOM from 'react-dom';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import { ClusterOptions, ExtendedMarkerData, IdType, TooltipElement } from '../../types';
import { getDataForMarker } from './markers-to-marker-data';
import { CLUSTER_ID } from '../hooks';
import { getPositionId } from '../hooks/use-clusters';
import { DEFAULT_MAX_ZOOM } from './utils';

import styles from './google-map-markers.module.scss';

export const getCluster = (
	map: google.maps.Map,
	markerInstanceMap: Map<IdType, google.maps.marker.AdvancedMarkerElement>,
	infoWindow: google.maps.InfoWindow,
	clusterOptions: ClusterOptions
) => {
	const markers: google.maps.marker.AdvancedMarkerElement[] = [];

	markerInstanceMap.forEach(markerInstance => {
		const data = getDataForMarker(markerInstance);
		if (data && data.shouldCluster) {
			markers.push(markerInstance);
		}
	});

	const onClusterClick = (
		event: any,
		position: google.maps.LatLng,
		clusterMarkers: google.maps.marker.AdvancedMarkerElement[],
		tooltip?: HTMLDivElement | string
	) => {
		event.stopPropagation();
		if (clusterOptions.onClusterClick) {
			const markerData = clusterMarkers.map(marker =>
				getDataForMarker(marker as google.maps.marker.AdvancedMarkerElement)
			);
			clusterOptions.onClusterClick?.(event, markerData as ExtendedMarkerData[]);
		}

		const ctrlKey = event.metaKey || event.ctrlKey;
		if (clusterOptions.getClusterTooltip && !ctrlKey) {
			openClusterInfoWindow({
				markers: clusterMarkers,
				position,
				infoWindow,
				map,
				clusterOptions,
				tooltip
			});
		} else if (ctrlKey) {
			infoWindow.set(CLUSTER_ID, null);
			infoWindow.close();
		}
	};

	const getMaxZoomMarker = (
		container: HTMLDivElement,
		clusterMarkers: google.maps.marker.AdvancedMarkerElement[],
		position: google.maps.LatLng,
		tooltip?: HTMLDivElement | string
	) => {
		const markersCount = clusterMarkers.length;
		const markersData = clusterMarkers.map(marker =>
			getDataForMarker(marker as google.maps.marker.AdvancedMarkerElement)
		);
		const groupedByIcon = markersData.reduce((acc, marker) => {
			const iconKey = marker?.data?.clusterIcon || BringgFontIcons.LocationFill;
			if (!acc[iconKey]) {
				acc[iconKey] = [];
			}
			acc[iconKey].push(marker);
			return acc;
		}, {} as Record<string, typeof markersData>);

		return ReactDOM.render(
			<div
				className={styles.clusterMaxZom}
				onClick={e => onClusterClick(e, position, clusterMarkers, tooltip)}
				data-test-id={`cluster-max-zoom-${markersCount}`}
			>
				{Object.entries(groupedByIcon).map(([icon, markers]) => (
					<div key={icon} className={styles.maxZoomIconTypeContainer}>
						{markers.length}
						<BringgIcon iconName={icon as BringgFontIcons} />
					</div>
				))}
			</div>,

			container
		);
	};

	const getMarker = (
		markersCount: number,
		clusterMarkers: google.maps.marker.AdvancedMarkerElement[],
		position: google.maps.LatLng
	) => {
		const container = document.createElement('div');
		if (!clusterMarkers || !clusterMarkers.length) {
			return container;
		}
		const markersData = clusterMarkers.map(marker =>
			getDataForMarker(marker as google.maps.marker.AdvancedMarkerElement)
		);

		const isInMaxZoom = map.getZoom() === DEFAULT_MAX_ZOOM;

		const allSameColor = markersData.every(marker => marker?.color === markersData[0]?.color);
		const color = allSameColor ? markersData[0]?.color : 'black';

		let tooltip: HTMLDivElement | string | undefined;
		if (clusterOptions.getClusterTooltip) {
			tooltip = clusterOptions.getClusterTooltip(
				clusterMarkers
					.map((marker: google.maps.marker.AdvancedMarkerElement) => getDataForMarker(marker))
					.filter(Boolean) as ExtendedMarkerData[]
			);
		}
		if (isInMaxZoom) {
			getMaxZoomMarker(container, clusterMarkers, position, tooltip);
		} else {
			ReactDOM.render(
				<div
					className={styles.cluster}
					style={{ ['--cluster-color' as string]: color }}
					onClick={e => onClusterClick(e, position, clusterMarkers, tooltip)}
					data-test-id={`cluster-${markersCount}`}
				>
					<span className={styles.markerText} style={{ ['--cluster-color' as string]: color }}>
						{markersCount}
					</span>
				</div>,

				container
			);
		}

		return container;
	};

	return new MarkerClusterer({
		map,
		markers,
		algorithm: new SuperClusterAlgorithm({ maxZoom: DEFAULT_MAX_ZOOM + 1 }),
		renderer: {
			render: ({ count, position, markers: clusterMarkers }) => {
				return new google.maps.marker.AdvancedMarkerElement({
					map,
					position,
					content: getMarker(count, clusterMarkers as google.maps.marker.AdvancedMarkerElement[], position)
				});
			}
		}
	});
};

export const openClusterInfoWindow = ({
	markers,
	position,
	infoWindow,
	map,
	clusterOptions,
	tooltip
}: {
	markers: google.maps.marker.AdvancedMarkerElement[];
	position: google.maps.LatLng;
	infoWindow: google.maps.InfoWindow;
	map: google.maps.Map;
	clusterOptions: ClusterOptions;
	id?: string;
	tooltip?: HTMLDivElement | string;
}) => {
	if (clusterOptions.getClusterTooltip) {
		const markersData = markers?.map(marker => getDataForMarker(marker));

		if (markersData.length) {
			const clusterId = getPositionId(position);
			infoWindow.set(CLUSTER_ID, clusterId);
			infoWindow.setPosition({ lat: position.lat(), lng: position.lng() });

			// Center the info window above the cluster
			infoWindow.setOptions({
				pixelOffset: new google.maps.Size(0, -26)
			});

			(tooltip as TooltipElement)?.renderTooltip?.();

			infoWindow.setContent(tooltip);
			infoWindow.open(map);
		}
	}
};
